import { useEffect, useMemo } from "react";
import * as PIXI from "pixi.js";

/**
 * The round UI buttons' soft drop shadow — ONE source (2026-10-05).
 *
 * Under the button's round texture (its circle, never its icon), so it reads
 * on light backgrounds. The same kind of shadow the UI text wears
 * (TEXT_STYLE_CONFIG.OUTLINE: a canvas drop shadow, soft, offset down-right),
 * a little stronger.
 *
 * Change it HERE and every button that uses it follows: ButtonBonusBuy,
 * ButtonIncrementBet, ButtonDecrementBet (secondary theme).
 *
 * Sizes are shares of the button's radius, so it scales with the button.
 * Drawn ONCE per size into a texture — nothing per frame.
 */
export const BUTTON_SHADOW = {
  ALPHA: 0.35, // how dark
  BLUR: 0.14, // how soft, x radius
  DISTANCE: 0.05, // how far it drops, x radius
  ANGLE: Math.PI / 4, // down-right, as the text's
};

/** Only the SHADOW of a circle of `radius`, on a canvas, as a texture. */
export const makeButtonShadowTexture = (radius, resolution = 1) => {
  const blur = radius * BUTTON_SHADOW.BLUR;
  const dist = radius * BUTTON_SHADOW.DISTANCE;
  const pad = Math.ceil(blur * 2 + dist + 2);
  const size = Math.ceil(radius * 2 + pad * 2);
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(size * resolution);
  canvas.height = Math.ceil(size * resolution);
  const ctx = canvas.getContext("2d");
  // The circle itself is drawn far off the canvas; only its shadow lands on
  // it. shadowBlur / shadowOffset are in DEVICE pixels (not transformed).
  const away = canvas.width * 4;
  ctx.shadowColor = `rgba(0, 0, 0, ${BUTTON_SHADOW.ALPHA})`;
  ctx.shadowBlur = blur * resolution;
  ctx.shadowOffsetX = -away + Math.cos(BUTTON_SHADOW.ANGLE) * dist * resolution;
  ctx.shadowOffsetY = Math.sin(BUTTON_SHADOW.ANGLE) * dist * resolution;
  ctx.beginPath();
  ctx.arc(canvas.width / 2 + away, canvas.height / 2, radius * resolution, 0, Math.PI * 2);
  ctx.fill();
  return PIXI.Texture.from(canvas, { resolution });
};

/**
 * The shadow texture for a round button of `radius` — rebuilt only when the
 * size changes, the old one freed. Put it in a Sprite (anchor 0.5) at the
 * button's centre, BEFORE the button's own sprite.
 */
export const useButtonShadow = (radius) => {
  const resolution = window.devicePixelRatio || 1;
  const texture = useMemo(
    () => (radius > 0 ? makeButtonShadowTexture(radius, resolution) : null),
    [radius, resolution],
  );
  useEffect(() => () => texture?.destroy(true), [texture]);
  return texture;
};

/**
 * The shadow of a button TEXTURE's own outline (any shape — circle, pill,
 * rounded rectangle), drawn at `width` x `height`, with BUTTON_SHADOW. The
 * texture's art is drawn far off the canvas; only its shadow lands. On a
 * round texture it is the circle's shadow exactly.
 *
 * @returns {{ texture: PIXI.Texture, pad: number, w: number, h: number }|null}
 *          `pad` is the margin around the button inside the shadow texture,
 *          `w` / `h` the shadow texture's logical size.
 */
export const makeTextureShadow = (source, width, height, resolution = 1) => {
  const image = source?.baseTexture?.resource?.source;
  const frame = source?.frame;
  if (!image || !frame || !(width > 0) || !(height > 0)) return null;
  const radius = Math.min(width, height) / 2;
  const blur = radius * BUTTON_SHADOW.BLUR;
  const dist = radius * BUTTON_SHADOW.DISTANCE;
  const pad = Math.ceil(blur * 2 + dist + 2);
  const w = Math.ceil(width + pad * 2);
  const h = Math.ceil(height + pad * 2);
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(w * resolution);
  canvas.height = Math.ceil(h * resolution);
  const ctx = canvas.getContext("2d");
  const away = canvas.width * 4;
  ctx.shadowColor = `rgba(0, 0, 0, ${BUTTON_SHADOW.ALPHA})`;
  ctx.shadowBlur = blur * resolution;
  ctx.shadowOffsetX = -away + Math.cos(BUTTON_SHADOW.ANGLE) * dist * resolution;
  ctx.shadowOffsetY = Math.sin(BUTTON_SHADOW.ANGLE) * dist * resolution;
  try {
    ctx.drawImage(
      image,
      frame.x,
      frame.y,
      frame.width,
      frame.height,
      pad * resolution + away,
      pad * resolution,
      width * resolution,
      height * resolution,
    );
  } catch {
    return null;
  }
  return { texture: PIXI.Texture.from(canvas, { resolution }), pad, w, h };
};
